import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { useAuth } from '../../../hooks/auth/useAuth';
import { AuthMode } from './AuthModal';
import EmailInput from '../EmailInput';
import PasswordInput from '../PasswordInput';
import LoadingButton from '../LoadingButton';
import styles from './AuthModal.module.css';

interface SignupContentProps {
  onSwitchMode: (mode: AuthMode) => void;
  onSuccess: () => void;
  redirectAfterAuth?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

// Password validation helper
function isPasswordValid(password: string): boolean {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
}

// Profile image URLs for random assignment - using local Tio character images
const PROFILE_IMAGE_URLS = [
  '/images/Tios/Growing-Tio.webp',
  '/images/Tios/Learning-Tio.webp', 
  '/images/Tios/Teaching-Tio.webp',
  '/images/Tios/Working-Tio.webp',
  '/images/Tios/Earning-Tio.webp',
  '/images/Tios/Star-Tio.webp',
  '/images/Tios/Thinking-Tio.webp',
  '/images/TioTransparent/Growing-Tio.webp',
  '/images/TioTransparent/Learning-Tio.webp',
  '/images/TioTransparent/Teaching-Tio.webp',
  '/images/TioTransparent/Working-Tio.webp',
  '/images/Tios/tio-sea.webp'
];

const SignupContent: React.FC<SignupContentProps> = ({ onSwitchMode, onSuccess, redirectAfterAuth }) => {
  const navigate = useNavigate();
  const { refreshUserProfile } = useAuth();
  // Form state
  const [values, setValues] = useState<FormValues>({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });

  // Validation and UI state
  const [errors, setErrors] = useState<Partial<FormValues>>({});
  const [touchedFields, setTouchedFields] = useState<Partial<Record<keyof FormValues, boolean>>>({});
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isPasswordLengthValid, setIsPasswordLengthValid] = useState(false);
  const [isPasswordSpecialCharValid, setIsPasswordSpecialCharValid] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [neutralMessage, setNeutralMessage] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // Loading states
  const { withLoading, isLoading } = useLoadingStates();
  const isGoogleLoading = isLoading('google');
  const isSignupLoading = isLoading('signup');

  // Capitalize first letter helper
  const capitalizeFirstLetter = (input: string) => {
    return input.charAt(0).toUpperCase() + input.slice(1);
  };

  // Validate individual field
  const validateField = (field: keyof FormValues, value: string) => {
    let error = '';
    
    if (field === 'firstName' || field === 'lastName') {
      if (!value.trim()) {
        error = `${field === 'firstName' ? 'First' : 'Last'} name is required`;
      }
    } else if (field === 'email') {
      if (!value.trim()) {
        error = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error = 'Please enter a valid email address';
      }
    } else if (field === 'password') {
      if (!value) {
        error = 'Password is required';
      } else if (!isPasswordValid(value)) {
        error = 'Password does not meet the required criteria';
      }
    }
    
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle field blur (when user leaves the field)
  const handleBlur = (field: keyof FormValues) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    validateField(field, values[field]);
  };

  // Handle input changes
  const handleInputChange = (field: keyof FormValues, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user types (but only if field was previously touched)
    if (errors[field] && touchedFields[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Special handling for names (capitalize)
    if (field === 'firstName' || field === 'lastName') {
      setValues(prev => ({ ...prev, [field]: capitalizeFirstLetter(value) }));
    }

    // Special handling for password validation
    if (field === 'password') {
      const hasLowerCase = /[a-z]/.test(value);
      const hasUpperCase = /[A-Z]/.test(value);
      const hasDigit = /\d/.test(value);
      const hasSpecialChar = /[@$!%*?&]/.test(value);
      
      setIsPasswordLengthValid(value.length >= 8);
      setIsPasswordSpecialCharValid(hasLowerCase && hasUpperCase && hasDigit && hasSpecialChar);
    }
  };

  // Email verification checker - using optimized auth import
  const checkEmailVerification = () => {
    const checkInterval = setInterval(async () => {
      const { auth } = await import('@/firebase/auth-providers');
      if (auth.currentUser) {
        auth.currentUser.reload()
          .then(() => {
            if (auth.currentUser?.emailVerified) {
              clearInterval(checkInterval);
              setIsEmailVerified(true);
              setVerificationMessage('Email successfully verified. Welcome on board! This box will now close.');
              setNeutralMessage('');
              
              // Welcome message already created during signup, no need to create again
              
              setTimeout(() => {
                if (redirectAfterAuth) {
                  navigate(redirectAfterAuth);
                  onSuccess(); // Still call onSuccess to close modal
                } else {
                  onSuccess();
                }
              }, 2000);
            } else {
              setNeutralMessage('Verification email sent. Please check your inbox and verify your email.');
            }
          })
          .catch((error) => {
            console.error('Error reloading user:', error);
          });
      } else {
        clearInterval(checkInterval);
      }
    }, 2000);

    return () => clearInterval(checkInterval);
  };

  // Google Sign Up - using optimized lazy-loading pattern
  const handleGoogleSignUp = async () => {
    console.log('🚀 Google signup called');
    try {
      await withLoading('google', async () => {
        // Dynamic import - lazy-loads resolver only when clicked
        // Same pattern as LoginContent for consistency and optimization
        const { signInWithGoogle } = await import('@/firebase/auth-providers');
        const result = await signInWithGoogle();
        
        if (result.user) {
          console.log('✅ Google signin successful, user:', result.user.uid);
          
          // Create complete user profile with 100 points
          try {
            const { getFirestoreLite, doc, setDoc, serverTimestamp } = await import('@/firebase/firestore-lite');
            const firestore = getFirestoreLite();
            
            // Extract name parts
            const firstName = result.user.displayName?.split(' ')[0] || '';
            const lastName = result.user.displayName?.split(' ')[1] || '';
            const email = result.user.email || '';
            
            // Generate unique custom URL for Google user
            console.log('🔗 Generating unique custom URL for Google user:', firstName);
            const { generateUniqueCustomURL } = await import('../../../utils/generateCustomURL');
            const customURL = await generateUniqueCustomURL(firstName, email);
            console.log('✅ Generated custom URL for Google user:', customURL);
            
            const userDocRef = doc(firestore, 'userProfiles', result.user.uid);
            const profileData = {
              firstName,
              lastName,
              displayName: result.user.displayName || '',
              email,
              photoURL: result.user.photoURL || '',
              customURL, // Add generated custom URL
              points: 100, // Initialize with 100 points for Google users too
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };
            console.log('📝 Creating Google user profile:', profileData);
            await setDoc(userDocRef, profileData, { merge: true });
            console.log('✅ Google user profile created successfully');
          } catch (error) {
            console.error('🚨 Error creating Google user profile:', error);
          }
          
          // Create welcome message
          try {
            console.log('🎯 Creating welcome message for Google user');
            const { createWelcomeMessage, hasWelcomeMessage } = await import('../../../services/WelcomeMessageService');
            
            // Only create welcome message if user doesn't already have one
            const alreadyHasWelcome = await hasWelcomeMessage(result.user.uid);
            if (!alreadyHasWelcome) {
              const welcomeResult = await createWelcomeMessage(result.user.uid);
              console.log('🎯 Welcome message result:', welcomeResult);
            } else {
              console.log('🎯 User already has welcome message, skipping');
            }
          } catch (error) {
            console.error('🚨 Error creating welcome message for Google user:', error);
            // Don't block the user flow if welcome message fails
          }
          
          // Refresh the auth context to pick up the new profile data
          console.log('🔄 Refreshing user profile for Google signup');
          await refreshUserProfile();
          
          // Google accounts are usually pre-verified, but check and handle accordingly
          if (result.user.emailVerified) {
            onSuccess(); // Close modal if already verified
          } else {
            // Send verification email for Google accounts that aren't verified
            try {
              const { sendEmailVerification } = await import('firebase/auth');
              await sendEmailVerification(result.user);
              onSwitchMode('verification');
            } catch (error) {
              console.error('Error sending verification email:', error);
              onSuccess(); // Fallback to closing modal
            }
          }
        }
      });
    } catch (error: any) {
      console.error('Google sign up error:', error);
      
      // User-friendly error messages matching LoginContent pattern
      let errorMessage = 'Google sign up failed. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign up cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized for Google Sign-up.';
      }
      
      setErrors({ email: errorMessage });
    }
  };

  // Email/Password Sign Up
  const handleSignUp = async () => {
    console.log('🚀 handleSignUp function called');
    const { firstName, lastName, email, password } = values;
    console.log('🚀 Form values:', { firstName, lastName, email, password: '***' });

    // Mark all fields as touched for validation
    setTouchedFields({
      firstName: true,
      lastName: true,
      email: true,
      password: true
    });

    // Validate all fields
    validateField('firstName', firstName);
    validateField('lastName', lastName);
    validateField('email', email);
    validateField('password', password);

    // Check if there are any validation errors
    if (!firstName || !lastName || !email || !password || !isPasswordValid(password)) {
      console.log('❌ Validation failed - stopping signup');
      return;
    }

    try {
      await withLoading('signup', async () => {
        // Get Firebase services - using optimized imports
        const { auth } = await import('@/firebase/auth-providers');
        const { getFirestoreLite } = await import('@/firebase/firestore-lite');
        const { doc, setDoc, serverTimestamp } = await import('firebase/firestore/lite');
        const firestore = getFirestoreLite();
        
        // Create user account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const fullName = `${firstName} ${lastName}`;
        
        // Select random profile image
        const randomIndex = Math.floor(Math.random() * PROFILE_IMAGE_URLS.length);
        const selectedProfileImageURL = PROFILE_IMAGE_URLS[randomIndex];

        // Update user profile
        await updateProfile(user, {
          displayName: fullName,
          photoURL: selectedProfileImageURL,
        });

        // Generate unique custom URL for the user
        console.log('🔗 Generating unique custom URL for:', firstName);
        const { generateUniqueCustomURL } = await import('../../../utils/generateCustomURL');
        const customURL = await generateUniqueCustomURL(firstName, email);
        console.log('✅ Generated custom URL:', customURL);

        // Create Firestore document with complete user profile
        console.log('📝 Creating user profile for:', user.uid);
        const userDocRef = doc(firestore, 'userProfiles', user.uid);
        const profileData = {
          firstName,
          lastName,
          displayName: fullName, // Match Firebase Auth displayName
          email,
          photoURL: selectedProfileImageURL,
          customURL, // Add generated custom URL
          points: 100, // Initialize with 100 points for new users
          createdAt: serverTimestamp(), // Add creation timestamp
          updatedAt: serverTimestamp()
        };
        console.log('📝 Profile data to write:', profileData);
        try {
          await setDoc(userDocRef, profileData, { merge: true });
          console.log('✅ User profile created successfully');
        } catch (profileError) {
          console.error('🚨 Error creating user profile:', profileError);
          throw profileError;
        }

        // Create welcome message immediately after user creation
        console.log('🎯 About to create welcome message for user:', user.uid);
        try {
          const { createWelcomeMessage } = await import('../../../services/WelcomeMessageService');
          const result = await createWelcomeMessage(user.uid);
          console.log('🎯 Welcome message result:', result);
        } catch (error) {
          console.error('🚨 Error creating welcome message:', error);
          // Don't block user creation if welcome message fails
        }

        // Send verification email (non-blocking)
        await sendEmailVerification(user);
        
        // Refresh the auth context to pick up the new profile data
        console.log('🔄 Refreshing user profile to update navbar');
        await refreshUserProfile();
        
        // Transition to verification mode instead of closing modal
        console.log('✅ Signup complete - transitioning to verification mode');
        onSwitchMode('verification');
      });
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: 'Email is already in use. Please use a different email address.' });
      } else {
        setErrors({ email: 'An error occurred during sign-up.' });
      }
      setVerificationMessage('');
    }
  };

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSignUp();
    }
  };

  // Check for current user on mount
  useEffect(() => {
    const checkCurrentUser = async () => {
      const { auth } = await import('@/firebase/auth-providers');
      if (auth.currentUser) {
        checkEmailVerification();
      }
    };
    checkCurrentUser();
  }, []);

  return (
    <div className={styles.formContainer}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>Sign up with email</h1>
          <p className={styles.authSubtitle}>
            Welcome to TioAssist.io
          </p>
        </div>

      {/* Email/Password Form */}
      <form onSubmit={(e) => { 
        console.log('📋 Form submitted');
        e.preventDefault(); 
        handleSignUp(); 
      }}>
        {/* First Name */}
        <div className={styles.inputGroup}>
          <EmailInput
            id="firstName-input"
            placeholder="First name"
            value={values.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            onKeyDown={handleKeyDown}
            hasError={!!errors.firstName && !!touchedFields.firstName}
            className={styles.formInput}
            autoComplete="given-name"
          />
          {errors.firstName && touchedFields.firstName && (
            <div className={styles.errorMessage}>
              {errors.firstName}
            </div>
          )}
        </div>

        {/* Last Name */}
        <div className={styles.inputGroup}>
          <EmailInput
            id="lastName-input"
            placeholder="Last name"
            value={values.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            onBlur={() => handleBlur('lastName')}
            onKeyDown={handleKeyDown}
            hasError={!!errors.lastName && !!touchedFields.lastName}
            className={styles.formInput}
            autoComplete="family-name"
          />
          {errors.lastName && touchedFields.lastName && (
            <div className={styles.errorMessage}>
              {errors.lastName}
            </div>
          )}
        </div>

        {/* Email */}
        <div className={styles.inputGroup}>
          <EmailInput
            id="email-input"
            placeholder="Email address"
            value={values.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            onKeyDown={handleKeyDown}
            hasError={!!errors.email && !!touchedFields.email}
            className={styles.formInput}
            autoComplete="email"
          />
          {errors.email && touchedFields.email && (
            <div className={styles.errorMessage}>
              {errors.email}
            </div>
          )}
        </div>

        {/* Password */}
        <div className={styles.inputGroup}>
          <PasswordInput
            id="password-input"
            placeholder="Create a password"
            value={values.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            onBlur={() => handleBlur('password')}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsPasswordFocused(true)}
            hasError={!!errors.password && !!touchedFields.password}
            className={styles.formInput}
            autoComplete="new-password"
          />
          {errors.password && touchedFields.password && (
            <div className={styles.errorMessage}>
              {errors.password}
            </div>
          )}
        </div>

        {/* Password Requirements - Encouraging Validation */}
        {isPasswordFocused && !isEmailSent && !verificationMessage && (
          <div style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
            <div style={{ 
              color: isPasswordLengthValid ? 'var(--c-success)' : 'var(--auth-text-secondary)', 
              marginBottom: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              {isPasswordLengthValid ? '✓' : '•'} At least 8 characters
            </div>
            <div style={{ 
              color: isPasswordSpecialCharValid ? 'var(--c-success)' : 'var(--auth-text-secondary)', 
              marginBottom: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              {isPasswordSpecialCharValid ? '✓' : '•'} Contains uppercase, lowercase, number, and special character
            </div>
          </div>
        )}

        {/* Form submission errors - can show multiple */}
        {errors.password && !isPasswordFocused && (
          <div style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
            <div style={{ color: 'var(--auth-error)' }}>
              {errors.password}
            </div>
          </div>
        )}

        {/* Status Messages */}
        {neutralMessage && (
          <div className={styles.infoMessage} style={{ marginBottom: '1rem' }}>
            {neutralMessage}
          </div>
        )}

        {verificationMessage && (
          <div className={styles.successMessage} style={{ marginBottom: '1rem' }}>
            {verificationMessage}
          </div>
        )}

        {/* Action Button */}
        {!isEmailVerified && !isEmailSent && (
          <LoadingButton
            variant="primary"
            onClick={() => {
              console.log('🔘 Continue button clicked');
              handleSignUp();
            }}
            loading={isSignupLoading}
            loadingText="Creating account..."
            className={styles.primaryButton}
          >
            Continue
          </LoadingButton>
        )}
      </form>

        {/* Footer */}
        <div className={styles.formFooter}>
          <p className={styles.switchModeText}>
            Already have an account?{' '}
            <span 
              className={styles.switchModeLink} 
              onClick={() => onSwitchMode('login')}
            >
              Sign in
            </span>
          </p>
        </div>
      </div>

      {/* Terms - Pushed to Bottom */}
      <div className={styles.termsText}>
        By signing up, you agree to our{' '}
        <a href="/terms-of-service" target="_blank" rel="noopener">Terms</a> and{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
      </div>
    </div>
  );
};

export default SignupContent;